/**
 * Chat Message Order
 *
 * Keeps chat transcript ordering and render identity stable.
 *
 * Primary responsibilities:
 * - Sort messages by their persisted thread order
 * - Preserve chronological fallback order for duplicate sequence numbers
 * - Merge older pages without dropping duplicate sequence rows
 * - Build unique render keys for transcript rows
 *
 * Dependencies:
 * - types
 */

import type { ChatMessage } from '@/types'

// --------------

function getCreatedAtTime(message: ChatMessage) {
  const createdAt = message.message.created_at
  if (!createdAt) return 0

  const timestamp = Date.parse(createdAt)
  return Number.isFinite(timestamp) ? timestamp : 0
}

export function compareChatMessagesByThreadOrder(
  left: ChatMessage,
  right: ChatMessage
) {
  const sequenceDiff = left.message.sequence_number - right.message.sequence_number
  if (sequenceDiff !== 0) return sequenceDiff

  return getCreatedAtTime(left) - getCreatedAtTime(right)
}

export function areChatMessagesInThreadOrder(messages: ChatMessage[]) {
  for (let index = 1; index < messages.length; index += 1) {
    const previous = messages[index - 1]
    const current = messages[index]

    if (
      previous &&
      current &&
      compareChatMessagesByThreadOrder(previous, current) > 0
    ) {
      return false
    }
  }

  return true
}

export function sortChatMessagesByThreadOrder(messages: ChatMessage[]) {
  return [...messages].sort(compareChatMessagesByThreadOrder)
}

export function mergeChatMessagesById(
  currentMessages: ChatMessage[],
  nextMessages: ChatMessage[]
) {
  const currentIds = new Set(
    currentMessages.map((chatMessage) => chatMessage.message.id)
  )
  const newMessages = nextMessages.filter(
    (chatMessage) => !currentIds.has(chatMessage.message.id)
  )

  return sortChatMessagesByThreadOrder([...newMessages, ...currentMessages])
}

export function getChatMessageRenderKey(chatMessage: ChatMessage) {
  // Prefer a stable client render key that survives the optimistic temp-id ->
  // real-DB-id swap at finalize. Without it, keying on message.id alone caused
  // the streaming message subtree to unmount/remount when the id changed at
  // finalize — which crashed new chats (DOM reconciliation error during the
  // finalize commit) and discarded the correctly-positioned streaming render
  // (reasoning/tool blocks), letting the thought jump to the bottom.
  // mergePersistedChatMessages stamps this key from the optimistic message so it
  // stays constant across the swap. Falls back to message.id (unique even when
  // sequence numbers collide), then to a chat+sequence slot.
  const renderKey = (chatMessage as { renderKey?: string }).renderKey?.trim()
  if (renderKey) return renderKey

  const messageId = chatMessage.message.id?.trim()
  if (messageId) return messageId

  return `${chatMessage.message.chat_id || 'chat'}-${chatMessage.message.sequence_number}`
}
